<template>
	<Row :gutter="[16, 16]">
		<Col :span="18">
			<div class="padding-lg-x">
				<StatsChartTitleWithDescription
					titleKey="paas.subheader.corrections"
					descriptionKey="paas.subheader.correctionsTooltip"
					titleClass="pd-md-top"
				/>
				<HeatMapChart
					bordered
					:isLoading="paasStore.isLoading"
					:series="heatmapMedianSeries"
					:categories="heatmapMedianCategories"
					:colorSettings="COLOR_SETTINGS_MEDIAN"
					xAxisLabel="RINEX date"
					units="mm"
				>
					<template #description>
						<span style="color: black">{{ $t('paas.noDataSubTitle') }}</span>
					</template>
				</HeatMapChart>

				<StatsChartTitleWithDescription
					titleKey="paas.subheader.stddev"
					descriptionKey="paas.subheader.stddevTooltip"
					titleClass="pd-md-top"
				/>
				<HeatMapChart
					bordered
					:isLoading="paasStore.isLoading"
					:series="heatmapStddevSeries"
					:categories="heatmapStddevCategories"
					:colorSettings="COLOR_SETTINGS_STDDEV"
					xAxisLabel="RINEX date"
					units="mm"
				>
					<template #description>
						<span style="color: black">{{ $t('paas.noDataSubTitle') }}</span>
					</template>
				</HeatMapChart>

				<StatsChartTitleWithDescription
					titleKey="paas.subheader.ranges"
					descriptionKey="paas.subheader.rangesTooltip"
					titleClass="pd-md-top"
				/>
				<TimeSeriesChart
					chartType="rangeArea"
					:isLoading="paasStore.isLoading"
					:series="rangeSeries"
					:options="rangeOptions"
					:height="rangeChartHeight"
				>
					<template #description>
						<span style="color: black">{{ $t('paas.noDataSubTitle') }}</span>
					</template>
				</TimeSeriesChart>
			</div>
		</Col>
		<Col :span="6">
			<VerticalLayout>
				<Card :bordered="false">
					<VerticalLayout>
						<div v-if="!pinnedMountpointRef">{{ $t('paas.mountpointsSelectHeader') }}</div>
						<Select
							v-show="!pinnedMountpointRef"
							class="width-100"
							v-model:value="mountpointsSelected"
							mode="multiple"
							:allowClear="true"
							:options="mountpointsOptions"
							@change="setSubnetworkOnMountpointsChange"
						/>
						<SubNetworkSelect
							ref="subNetworkSelect"
							v-show="!pinnedMountpointRef"
							:onChange="setMountpointsForSubnetworkLocal"
							:allowClear="true"
						/>
						<Space>
							{{ $t('paas.valuesCountHeader') }}
							<InputDecimal
								v-model="valuesCount"
								range="all"
								:minValue="1"
								:maxValue="14"
								:allowPoint="false"
								:disabled="!hasData"
							/>
						</Space>
						<Button type="primary" @click="onApplyFromChart" :disabled="isProcessing || !hasData">
							{{ $t('paas.buttons.applyFromChart', mountpoints.length) }}
						</Button>
					</VerticalLayout>
				</Card>
				<div class="scrollable-x">
					<Table
						:columns="columns"
						:data-source="correctionValues"
						:pagination="false"
						size="small"
					/>
				</div>
			</VerticalLayout>
		</Col>
	</Row>
</template>

<script>
import { ref, toRef, computed } from 'vue';
import { Table, Button, Space, Row, Col, Card, Select, message } from 'ant-design-vue';

import { useI18n } from 'vue-i18n'

import { usePaasStore } from '@/stores/paas';

import VerticalLayout from '@/components/common/VerticalLayout.vue';
import TimeSeriesChart from '@/components/stats/TimeSeriesChart.vue';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';
import HeatMapChart from '@/components/charts/HeatMapChart.vue';
import InputDecimal from '@/components/common/InputDecimal.vue';
import SubNetworkSelect from '@/components/common/controls/SubNetworkSelect.vue';
import { calcCorrectionValues, usePaasChart } from '@/components/paas/paasUtils';
import { showErrorMessage } from '@/components/common/errorMessage';
import { useSubNetworkMountpointsFilter } from '@/utils/useSubNetworkMountpointsFilter';

import ApiPaas from '@/api/ApiPaas';
import Consts from '@/api/Consts';

import TranslationUtils from '@/i18n/TranslationUtils';


const CORRECTION_VALUES_COLUMNS = [
	{ dataIndex: 'mountpoint' },
	{ dataIndex: 'distance', align: 'right' },
	{ dataIndex: 'std', align: 'right' },
];


const COLOR_SETTINGS_MEDIAN = [
	{ maxValue: 15, color: '5ebced', label: '< 15 mm' },
	{ maxValue: 25, color: '3dc50b', label: '15 – 25' },
	{ maxValue: 35, color: 'eec548', label: '25 – 35' },
	{ maxValue: 100000000, color: 'ff1f1c', label: '> 35 mm' },
];


const COLOR_SETTINGS_STDDEV = [
	{ maxValue: 20, color: '5ebced', label: '< 20 mm' },
	{ maxValue: 30, color: '3dc50b', label: '20 – 30' },
	{ maxValue: 40, color: 'eec548', label: '30 – 40' },
	{ maxValue: 100000000, color: 'ff1f1c', label: '≥ 40 mm' },
];


export default {
	components: {
		Button, Table, Space, Row, Col,
		Card, Select, InputDecimal,
		VerticalLayout, TimeSeriesChart, StatsChartTitleWithDescription, HeatMapChart, SubNetworkSelect,
	},
	props: {
		pinnedMountpoint: {
			type: String,
			required: false,
			default: '',
		},
		minDate: {
			type: Date,
			required: true,
		},
		maxDate: {
			type: Date,
			required: true,
		},
	},
	emits: ['corrected'],
	setup(props, { emit }) {
		const paasStore = usePaasStore();
		const { t } = useI18n();

		const mountpointsSelected = ref([]);
		const pinnedMountpointRef = toRef(props, 'pinnedMountpoint');
		const mountpoints = computed(() => {
			if (pinnedMountpointRef.value) {
				return [pinnedMountpointRef.value];
			}
			return mountpointsSelected.value.length > 0 ? mountpointsSelected.value : paasStore.mountpoints;
		});
		const mountpointsOptions = computed(() => paasStore.mountpoints.map(mountpoint => ({
			value: mountpoint,
			label: mountpoint,
		})));

		const {
			subNetworkSelect,
			setMountpointsForSubnetwork,
			setSubnetworkOnMountpointsChange,
		} = useSubNetworkMountpointsFilter(mountpointsSelected, () => {});
		const setMountpointsForSubnetworkLocal = value => {
			setMountpointsForSubnetwork(value);
			mountpointsSelected.value = mountpointsSelected.value
				.filter(mountpoint => paasStore.mountpoints.includes(mountpoint));
		};

		const minDate = toRef(props, 'minDate');
		const maxDate = toRef(props, 'maxDate');
		const {
			rangeChartHeight,
			rangeSeries, rangeOptions,
			heatmapMedianSeries, heatmapMedianCategories,
			heatmapStddevSeries, heatmapStddevCategories,
			hasData,
		} = usePaasChart(paasStore, mountpoints, minDate, maxDate, t);

		const valuesCount = ref(1);
		const correctionValues = computed(() => calcCorrectionValues(mountpoints.value, paasStore.paasCoords, valuesCount.value));

		const isProcessing = ref(false);
		const onApplyFromChart = () => {
			isProcessing.value = true;
			ApiPaas.paasTaskApplyFromChart(
				calcCorrectionValues(mountpoints.value, paasStore.paasCoords, valuesCount.value),
				response => {
					isProcessing.value = false;
					if (response.status === 'ok') {
						message.success(t('paas.messages.appliedFromChart'));
						emit('corrected');
					} else {
						showErrorMessage(response.errors);
					}
				},
			);
		};
		return {
			paasStore,
			columns: TranslationUtils.addTranslatedField(
				CORRECTION_VALUES_COLUMNS,
				'dataIndex', 'title',
				'paas.correctionValuesTableColumns',
			),
			isProcessing,
			mountpointsSelected,
			pinnedMountpointRef,
			mountpoints,
			mountpointsOptions,
			subNetworkSelect,
			setMountpointsForSubnetworkLocal,
			setSubnetworkOnMountpointsChange,

			rangeChartHeight,
			rangeSeries, rangeOptions,
			heatmapMedianSeries, heatmapMedianCategories,
			heatmapStddevSeries, heatmapStddevCategories,
			hasData,
			COLOR_SETTINGS_MEDIAN: ref(COLOR_SETTINGS_MEDIAN),
			COLOR_SETTINGS_STDDEV: ref(COLOR_SETTINGS_STDDEV),

			valuesCount,
			correctionValues,
			onApplyFromChart,
			colorBlack: Consts.COLORS.dark0,
		};
	},
}
</script>
